import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosGovernanceService, RorosBudgetLedgerService } from '@/services/RorosService';
import { rorosKeys } from './keys';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosConstitution() {
  return useQuery({
    queryKey: rorosKeys.governance.constitution,
    queryFn: () => RorosGovernanceService.getConstitution(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosDecisionRights() {
  return useQuery({
    queryKey: rorosKeys.governance.decisionRights,
    queryFn: () => RorosGovernanceService.getDecisionRights(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosExplorationZones(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.governance.explorationZones, params] as const,
    queryFn: () => RorosGovernanceService.listExplorationZones(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosExplorationZone(id: string) {
  return useQuery({
    queryKey: rorosKeys.governance.explorationZone(id),
    queryFn: () => RorosGovernanceService.getExplorationZone(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSourceOfTruth() {
  return useQuery({
    queryKey: rorosKeys.governance.sourceOfTruth,
    queryFn: () => RorosGovernanceService.getSourceOfTruth(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosConstitutionAdd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosGovernanceService.addConstitutionEntry(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.governance.constitution });
    },
  });
}

export function useRorosConstitutionUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosGovernanceService.updateConstitutionEntry(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.governance.constitution });
    },
  });
}

export function useRorosConstitutionDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosGovernanceService.deleteConstitutionEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.governance.constitution });
    },
  });
}

export function useRorosDecisionRightsUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosGovernanceService.updateDecisionRights(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.governance.decisionRights });
    },
  });
}

export function useRorosExplorationZoneCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosGovernanceService.createExplorationZone(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.governance.explorationZones });
    },
  });
}

export function useRorosExplorationZoneRevoke() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosGovernanceService.revokeExplorationZone(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.governance.explorationZones });
      qc.invalidateQueries({ queryKey: rorosKeys.governance.explorationZone(id) });
    },
  });
}

export function useRorosExplorationZoneClose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decisionNoteId }: { id: string; decisionNoteId: string }) =>
      RorosGovernanceService.closeExplorationZone(id, { decision_note_id: decisionNoteId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.governance.explorationZones });
    },
  });
}

// ─── Budget Ledger (J7-W2 task 5) ──────────────────────────────────────────

export function useRorosBudgetLedger(params?: { actorId?: string | null; days?: number | null }) {
  return useQuery({
    queryKey: rorosKeys.governance.budgetLedger(params?.actorId ?? null, params?.days ?? null),
    queryFn: () =>
      RorosBudgetLedgerService.get({
        actorId: params?.actorId ?? undefined,
        days: params?.days ?? undefined,
      }),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}
